import { Controller } from "@hotwired/stimulus";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk"

export default class extends Controller {
  static values = {
    clientKey: String,
    customerKey: String,
    orderExtId: String,
    amount: Number,
    orderName: String,
    customerEmail: String,
    customerName: String,
    customerPhone: String,
    successUrl: String,
    failUrl: String,
    identityVerified: { type: Boolean, default: false }
  };

  static targets = [
    "paymentButton"
  ];

  async connect() {
    // [FIX] 연결 시점에는 disconnected 상태가 아님을 명시
    this.isDisconnected = false;
    await this.initializePaymentWidget();
  }

  async initializePaymentWidget() {
    // [FIX] 비동기 호출 사이에 컨트롤러가 해제되었는지 체크하는 가드(Guard) 추가

    // 1. SDK 로드
    const tossPayments = await loadTossPayments(this.clientKeyValue);
    if (this.isDisconnected) return; // 이미 페이지를 떠났다면 중단

    this.tossPayments = tossPayments;
    this.widgets = this.tossPayments.widgets({
      customerKey: this.customerKeyValue
    });

    // 2. 금액 설정
    await this.widgets.setAmount({
      currency: 'KRW',
      value: this.amountValue
    });
    if (this.isDisconnected) return;

    // [FIX] Turbo 캐싱 등으로 인해 기존 DOM에 내용이 남아있을 수 있으므로 초기화
    const paymentMethodEl = document.querySelector('#payment-method');
    const agreementEl = document.querySelector('#agreement');
    if (paymentMethodEl) paymentMethodEl.innerHTML = '';
    if (agreementEl) agreementEl.innerHTML = '';

    // 3. 결제 수단 렌더링
    try {
      this.paymentMethodWidget = await this.widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'DEFAULT'
      });
    } catch (error) {
      console.error("Payment Method Render Error:", error);
    }
    if (this.isDisconnected) return;

    // 4. 약관 렌더링
    try {
      this.agreementWidget = await this.widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT'
      });
    } catch (error) {
      console.error("Agreement Render Error:", error);
    }
    if (this.isDisconnected) return;

    if (this.identityVerifiedValue) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    if (!this.agreementWidget) return; // 안전장치

    // 약관 동의 상태 변경 이벤트
    this.agreementWidget.on('agreementStatusChange', (agreementStatus) => {
      const agreed = agreementStatus.agreedRequiredTerms;
      this.paymentButtonTarget.disabled = !agreed;

      if (agreed) {
        this.paymentButtonTarget.innerText = "결제하기";
      } else {
        this.paymentButtonTarget.innerText = "이용약관을 먼저 동의 해주세요";
      }

      this.paymentButtonTarget.classList.toggle("noon-yellow-button", agreed)
      this.paymentButtonTarget.classList.toggle("noon-disabled-button", !agreed)
    });

    // 기본 동의이므로, 본인인증 활성화 처리 해주기
    this.paymentButtonTarget.classList.toggle("noon-yellow-button", true)
    this.paymentButtonTarget.classList.toggle("noon-disabled-button", false)
  }

  requestPayment() {
    const tosspaymentsOrderId = this.generateUUID();
    if (this.paymentButtonTarget.disabled) {
      this.showError('약관에 동의해주세요.');
      return;
    }

    this.setButtonLoading(true);

    // Promise를 사용하지 않고 successUrl, failUrl로 리다이렉트 방식 사용
    // 모바일에서 Promise 방식을 지원하지 않으므로 URL 리다이렉트 방식으로 처리
    this.widgets.requestPayment({
      orderId: tosspaymentsOrderId,
      orderName: this.orderNameValue,
      customerEmail: this.customerEmailValue,
      customerName: this.customerNameValue,
      customerMobilePhone: this.customerPhoneValue,
      successUrl: this.successUrlValue,
      failUrl: this.failUrlValue
    }).catch((error) => {
      // 결제창 자체에서 발생하는 에러만 처리 (사용자 취소 등)
      console.error('결제 요청 실패:', error);
      if (!this.isDisconnected) {
        this.setButtonLoading(false);
        if (error.code && error.code !== 'USER_CANCEL') {
          this.showError(`결제 실패: ${error.message}`);
        }
      }
    });
  }

  setButtonLoading(loading) {
    if (!this.hasPaymentButtonTarget) return; // 타겟 존재 여부 확인

    this.paymentButtonTarget.disabled = loading;

    if (loading) {
      this.paymentButtonTarget.textContent = '결제 진행 중...';
      this.paymentButtonTarget.classList.add('opacity-75', 'cursor-not-allowed');
    } else {
      this.paymentButtonTarget.textContent = '결제하기';
      this.paymentButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed');
    }
  }

  get flashesController() {
    return this.application.getControllerForElementAndIdentifier(document.getElementById("flashesContainer"), "components--flashes")
  }

  showError(message) {
    if (this.flashesController) {
      let flashParams = { ttl: 15, stack: false, key: "error" };
      flashParams.message = message;
      this.flashesController.createFlash(flashParams);
    } else {
      alert(message); // Fallback
    }
  }

  // crypto.randomUUID()가 지원되지 않는 브라우저를 위한 폴백
  generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // 폴백: crypto.getRandomValues 사용
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  }

  disconnect() {
    // [FIX] 플래그 설정: 진행 중인 비동기 작업들이 중단되도록 함
    this.isDisconnected = true;

    // 컨트롤러가 해제될 때 정리 작업
    if (this.paymentMethodWidget) {
      try {
        // [참고] Toss SDK 버전에 따라 destroy가 Promise를 반환할 수도 있으나
        // disconnect는 동기적이므로 await를 사용할 수 없음.
        this.paymentMethodWidget.destroy();
      } catch (error) {
        console.warn('결제위젯 정리 중 오류:', error);
      }
    }

    // 명시적으로 null 처리하여 GC 유도
    this.paymentMethodWidget = null;

    if (this.agreementWidget) {
      try {
        this.agreementWidget.destroy();
      } catch (error) {
        console.warn('약관위젯 정리 중 오류:', error);
      }
    }
    this.agreementWidget = null;
    this.widgets = null;
  }
}